import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Types
export interface ReportField {
  entity: string;
  field: string;
  label: string;
  format?: 'currency' | 'date' | 'datetime' | 'number' | 'text';
}

export interface ReportGroupBy {
  entity: string;
  field: string;
}

export interface ReportOrderBy {
  entity: string;
  field: string;
  direction: 'asc' | 'desc';
}

export interface ReportAggregation {
  type: 'count' | 'sum' | 'avg' | 'min' | 'max';
  entity: string;
  field: string;
  alias: string;
}

export interface ReportConfig {
  entities: string[];
  fields: ReportField[];
  filters?: Record<string, any>;
  groupBy?: ReportGroupBy[];
  orderBy?: ReportOrderBy[];
  aggregations?: ReportAggregation[];
}

export interface CustomReport {
  id: string;
  organization_id: string;
  created_by: string;
  name: string;
  description?: string;
  config: ReportConfig;
  is_shared: boolean;
  is_scheduled: boolean;
  schedule_config?: any;
  last_run_at?: string;
  run_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateReportInput {
  organization_id: string;
  name: string;
  description?: string;
  config: ReportConfig;
  is_shared?: boolean;
}

export interface UpdateReportInput {
  name?: string;
  description?: string;
  config?: ReportConfig;
  is_shared?: boolean;
}

// Fetch all reports for an organization
export function useCustomReports(organizationId: string) {
  return useQuery({
    queryKey: ['custom-reports', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_reports')
        .select('*')
        .eq('organization_id', organizationId)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data as CustomReport[];
    },
    enabled: !!organizationId,
  });
}

// Fetch a single report by ID
export function useCustomReport(reportId: string) {
  return useQuery({
    queryKey: ['custom-reports', reportId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_reports')
        .select('*')
        .eq('id', reportId)
        .single();

      if (error) throw error;
      return data as CustomReport;
    },
    enabled: !!reportId,
  });
}

// Create a new report
export function useCreateCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateReportInput) => {
      const { data, error } = await supabase
        .from('custom_reports')
        .insert({
          organization_id: input.organization_id,
          name: input.name,
          description: input.description,
          config: input.config,
          is_shared: input.is_shared ?? false,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as CustomReport;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports', data.organization_id] });
    },
  });
}

// Update an existing report
export function useUpdateCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateReportInput }) => {
      const { data, error } = await supabase
        .from('custom_reports')
        .update(input)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as CustomReport;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports', data.organization_id] });
      queryClient.invalidateQueries({ queryKey: ['custom-reports', data.id] });
    },
  });
}

// Delete a report
export function useDeleteCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('custom_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports'] });
    },
  });
}

// Duplicate a report
export function useDuplicateCustomReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: string) => {
      // Fetch the original report
      const { data: original, error: fetchError } = await supabase
        .from('custom_reports')
        .select('*')
        .eq('id', reportId)
        .single();

      if (fetchError) throw fetchError;

      // Create a copy with modified name
      const { data, error } = await supabase
        .from('custom_reports')
        .insert({
          organization_id: original.organization_id,
          name: `${original.name} (Copy)`,
          description: original.description,
          config: original.config,
          is_shared: false,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as CustomReport;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports', data.organization_id] });
    },
  });
}

// Execute a report (generates the actual data)
export function useExecuteReport() {
  return useMutation({
    mutationFn: async (reportId: string) => {
      // This will call a backend function to execute the report
      // For now, we'll implement a simple version
      const { data: report, error: reportError } = await supabase
        .from('custom_reports')
        .select('*')
        .eq('id', reportId)
        .single();

      if (reportError) throw reportError;

      // Execute based on entity type
      const config = report.config as ReportConfig;
      const entity = config.entities[0]; // Single entity for now

      // Always fetch all detail rows (we'll group them on the frontend)
      let query = supabase.from(entity).select('*');

      // Apply filters if present
      if (config.filters && config.filters[entity]) {
        const filters = config.filters[entity];
        Object.entries(filters).forEach(([key, value]) => {
          if (value === null || value === undefined) return;

          // Handle array filters (multi-select)
          if (Array.isArray(value) && value.length > 0) {
            query = query.in(key, value);
          }
          // Handle range filters
          else if (key.endsWith('_min')) {
            const fieldName = key.replace('_min', '');
            query = query.gte(fieldName, value);
          } else if (key.endsWith('_max')) {
            const fieldName = key.replace('_max', '');
            query = query.lte(fieldName, value);
          } else if (key.endsWith('_from')) {
            const fieldName = key.replace('_from', '');
            query = query.gte(fieldName, value);
          } else if (key.endsWith('_to')) {
            const fieldName = key.replace('_to', '');
            query = query.lte(fieldName, value);
          }
          // Handle boolean and single value filters
          else if (typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number') {
            query = query.eq(key, value);
          }
        });
      }

      // Apply ordering
      if (config.orderBy && config.orderBy.length > 0) {
        config.orderBy.forEach((order) => {
          query = query.order(order.field, { ascending: order.direction === 'asc' });
        });
      }

      const { data, error } = await query;
      if (error) throw error;

      // Log execution
      await supabase.from('custom_report_executions').insert({
        report_id: reportId,
        executed_by: (await supabase.auth.getUser()).data.user?.id,
        status: 'completed',
        result_count: data?.length || 0,
        execution_time_ms: 0, // Would need to measure this properly
      });

      return {
        report,
        data,
        config,
      };
    },
  });
}

// Get report execution history
export function useReportExecutions(reportId: string) {
  return useQuery({
    queryKey: ['report-executions', reportId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_report_executions')
        .select('*')
        .eq('report_id', reportId)
        .order('executed_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    },
    enabled: !!reportId,
  });
}
